import { useEffect, useState } from 'react';
import { WebCallButton } from '../components/WebCall';
import { ApiError, type AgentRow, type Api, type CallTurn, type TestRoom, type VoiceInfo, agentTitle } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';
import { ReadOnlyNote } from '../ui';
import { CallCard } from './CallDetail';
import { when } from './Calls';

/**
 * ADR-035 test room: the business tries an agent against a simulated customer — two voices on a real
 * (internal) line, recorded like a real call. No phone number, no line minutes.
 */
export const TestRoomPage = ({ api, t, onOpen }: { api: Api; t: T; onOpen: (id: string) => void }) => {
  // ADR-071: starting a room or a web call needs centeros.call (the page is also gated in App);
  // "what the agent heard" reads the transcript, which needs centeros.recordings.
  const can = useCan();
  const canCall = can(PERM.call);
  const canHear = can(PERM.recordings);
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [personas, setPersonas] = useState<string[]>([]);
  const [voices, setVoices] = useState<VoiceInfo[]>([]);
  const [rooms, setRooms] = useState<TestRoom[]>();
  const [agentId, setAgentId] = useState('');
  const [personaId, setPersonaId] = useState('');
  const [direction, setDirection] = useState<'outbound' | 'inbound'>('outbound');
  const [scenario, setScenario] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [open, setOpen] = useState<string>();
  // ADR-063: who talks to the agent — the simulated customer, or you with the microphone.
  const [who, setWho] = useState<'sim' | 'me'>('sim');

  const load = () => api.rooms().then((r) => setRooms(r.rooms)).catch(() => setRooms([]));
  useEffect(() => {
    void api.agents().then((r) => {
      const ready = r.items.filter((a) => a.publishedPackageId);
      setAgents(ready);
      setAgentId((x) => x || ready[0]?.id || '');
    }).catch(() => undefined);
    void api.roomPersonas().then((r) => {
      setPersonas(r.personas);
      setPersonaId((x) => x || r.personas[0] || '');
    }).catch((e: unknown) => setError(e instanceof ApiError ? e.code : 'generic'));
    void api.voices().then((r) => setVoices(r.voices)).catch(() => undefined);
    void load();
  }, [api]);

  // While a room is running, its status is refreshed.
  const running = (rooms ?? []).some((r) => r.status === 'created' || r.status === 'live');
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => void load(), 4000);
    return () => clearInterval(id);
  }, [running]);

  const start = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const r = await api.startRoom({ agentId, personaId, direction, ...(scenario.trim() ? { scenario: scenario.trim() } : {}), ...(voiceId ? { voiceId } : {}) });
      await load();
      setOpen(r.roomId);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'generic');
    } finally {
      setBusy(false);
    }
  };

  const agentName = (id: string) => ((a) => (a ? agentTitle(a) : id))(agents.find((a) => a.id === id));
  const current = rooms?.find((r) => r.id === open);

  return (
    <section>
      <header className="page-head">
        <div>
          <h1>{t('room.title')}</h1>
          <p className="muted">{t('room.subtitle')}</p>
        </div>
      </header>
      <ReadOnlyNote t={t} need={PERM.call} />
      <div className="room-mode" role="tablist">
        <button type="button" role="tab" aria-selected={who === 'sim'} className={who === 'sim' ? 'active' : ''} onClick={() => setWho('sim')}>{t('room.mode.sim')}</button>
        <button type="button" role="tab" aria-selected={who === 'me'} className={who === 'me' ? 'active' : ''} onClick={() => setWho('me')}>🎙 {t('room.mode.me')}</button>
      </div>
      {who === 'me' && (
        <div className="split">
          <div className="split-list">
            <div className="form-grid one">
              <label>{t('room.agent')}
                <select value={agentId} onChange={(e) => setAgentId(e.target.value)}>
                  {agents.map((a) => <option key={a.id} value={a.id}>{agentTitle(a)}</option>)}
                </select>
              </label>
              {agents.length === 0 && <p className="hint">{t('room.noAgents')}</p>}
              <p className="hint">{t('room.mode.me.hint')}</p>
            </div>
          </div>
          <div>
            {canCall && <WebCallButton key={agentId} inline api={api} t={t} agentId={agentId} agentName={agentName(agentId)} onOpenCall={onOpen} />}
          </div>
        </div>
      )}
      {who === 'sim' && (
      <div className="split">
        <div className="split-list">
          <div className="form-grid one">
            <label>{t('room.agent')}
              <select value={agentId} onChange={(e) => setAgentId(e.target.value)}>
                {agents.map((a) => <option key={a.id} value={a.id}>{agentTitle(a)}</option>)}
              </select>
            </label>
            {agents.length === 0 && <p className="hint">{t('room.noAgents')}</p>}
            <label>{t('room.direction')}
              <select value={direction} onChange={(e) => setDirection(e.target.value as 'outbound' | 'inbound')}>
                <option value="outbound">{t('room.direction.outbound')}</option>
                <option value="inbound">{t('room.direction.inbound')}</option>
              </select>
            </label>
            <label>{t('room.persona')}
              <select value={personaId} onChange={(e) => setPersonaId(e.target.value)}>
                {personas.map((p) => <option key={p} value={p}>{t(`room.persona.${p}`)}</option>)}
              </select>
            </label>
            {personaId && <p className="hint">{t(`room.persona.${personaId}.hint`)}</p>}
            <label>{t('room.scenario')}
              <textarea rows={3} maxLength={1000} value={scenario} placeholder={t('room.scenario.placeholder')} onChange={(e) => setScenario(e.target.value)} />
            </label>
            <p className="hint">{t('room.scenario.hint')}</p>
            <label>{t('room.voice')}
              <select value={voiceId} onChange={(e) => setVoiceId(e.target.value)}>
                <option value="">{t('room.voice.default')}</option>
                {voices.map((v) => <option key={v.voiceId} value={v.voiceId}>{v.name}{v.gender ? ` · ${v.gender}` : ''}{v.accent ? ` · ${v.accent}` : ''}</option>)}
              </select>
            </label>
            <button type="button" className="primary" disabled={busy || !agentId || !personaId || !canCall} title={canCall ? undefined : t('error.PERMISSION_DENIED')} onClick={() => void start()}>{busy ? t('room.starting') : t('room.start')}</button>
            <p className="hint">{t('room.cost')}</p>
            {error && <div className="banner error small">{t(`error.${error}`) === `error.${error}` ? error : t(`error.${error}`)}</div>}
          </div>
          <h3 className="list-title">{t('room.history')}</h3>
          {rooms && rooms.length === 0 && <p className="muted small">{t('room.empty')}</p>}
          <ul className="room-list">
            {(rooms ?? []).map((r) => (
              <li key={r.id}>
                <button type="button" className={`room-item ${open === r.id ? 'active' : ''}`} onClick={() => setOpen(r.id)}>
                  <span className={`room-status ${r.status}`}>{t(`room.status.${r.status}`)}</span>
                  <strong>{agentName(r.agentId)}</strong>
                  <span className="muted small">{t(`room.persona.${r.customer.personaId}`)} · {t(`room.direction.${r.direction}`)} · {when(r.createdAt)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div>
          {!current && <div className="empty"><p>{t('room.pick')}</p></div>}
          {current && (current.status === 'created' || current.status === 'live') && (
            <div className="banner info">{t('room.running')}</div>
          )}
          {current && current.status === 'failed' && <div className="banner error">{t('room.failed')}</div>}
          {current && current.status === 'ended' && (
            <>
              {canHear && <HeardCheck api={api} t={t} room={current} />}
              <CallCard key={current.callId} api={api} t={t} callId={current.callId} onOpen={onOpen} />
            </>
          )}
        </div>
      </div>
      )}
    </section>
  );
};

/** Words without marks: speech-to-text may add vowel signs (Arabic harakat) the speaker's text does not have. */
const words = (x: string): string[] => x.normalize('NFKD').replace(/\p{M}+/gu, '').toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean);
/**
 * Pairs each line the customer said with what the agent heard, in order, by word overlap (any language):
 * a line the agent never heard stays empty instead of shifting the rest.
 */
export const matchHeard = (said: string[], heard: string[]): Array<string | undefined> => {
  let from = 0;
  return said.map((s) => {
    const a = new Set(words(s));
    for (let j = from; j < heard.length; j += 1) {
      const b = words(heard[j]!);
      const common = b.filter((w) => a.has(w)).length;
      if (a.size && common / Math.max(a.size, b.length) >= 0.3) {
        from = j + 1;
        return heard[j];
      }
    }
    return undefined;
  });
};

/** What the simulated customer said next to what the agent heard (speech-to-text check), line by line. */
const HeardCheck = ({ api, t, room }: { api: Api; t: T; room: TestRoom }) => {
  const [turns, setTurns] = useState<CallTurn[]>([]);
  useEffect(() => void api.callDetail(room.callId).then((r) => setTurns(r.turns)).catch(() => undefined), [api, room.callId]);
  const heard = matchHeard(room.said, turns.map((x) => x.customer).filter((x): x is string => !!x));
  if (room.said.length === 0) return null;
  return (
    <div className="card heard-check">
      <h3>{t('room.heard.title')}</h3>
      <p className="hint">{t('room.heard.hint')}</p>
      <table className="table">
        <thead><tr><th>{t('room.heard.said')}</th><th>{t('room.heard.heard')}</th></tr></thead>
        <tbody>
          {room.said.map((s, i) => (
            <tr key={i}><td>{s}</td><td className={heard[i] ? '' : 'muted'}>{heard[i] ?? t('room.heard.none')}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
